import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { adjustArmNow, getArmPreview } from 'services'
import { confirm, formatDate } from 'utils'

interface ArmPreview {
  nextAdjustmentDate: string
  termsComplete: boolean
  indexValue: number
  indexSource: string
  indexMissing: string
  fullyIndexedRate: number
  newRate: number
  currentRate: number
  appliedLimits: string[]
}

/**
 * One line under the Adjustable Rate group saying what the next adjustment will do: which index
 * value it reads, the fully indexed rate, and where the caps leave the loan. Read-only, from the
 * same calculation the adjustment runs, so a missing index value or unfilled terms show up here
 * before the adjustment date rather than as a skipped loan after it.
 *
 * `refreshKey` changes whenever a saved ARM term changes, so the line follows the servicer's edits.
 *
 * "Adjust now" schedules that next adjustment straight away instead of waiting for the nightly run
 * to reach it, for a servicer who wants it (and its borrower notice) out early. It still takes
 * effect on its own date. `onAdjusted` lets the screen reload the ARM dates the adjustment moved.
 */
export const ArmAdjustmentPreview = ({ refreshKey, onAdjusted }: { refreshKey: string; onAdjusted?: () => void }) => {
  const [preview, setPreview] = useState<ArmPreview | null>(null)
  const [adjusting, setAdjusting] = useState(false)
  const [reload, setReload] = useState(0)
  const { hasPermission } = usePermissions()
  const canAdjust = hasPermission('Loan.Servicing.ServicingInformation.Terms.Edit')

  useEffect(() => {
    let cancelled = false
    getArmPreview()
      .then((res: ArmPreview) => !cancelled && setPreview(res))
      .catch(() => !cancelled && setPreview(null))
    return () => {
      cancelled = true
    }
  }, [refreshKey, reload])

  if (!preview) return null
  const when = preview.nextAdjustmentDate ? formatDate(preview.nextAdjustmentDate) : ''

  let text: string
  let tone = 'bg-slate-50 text-slate-700 border-slate-200'
  if (!when) {
    text = 'No next adjustment date is set, so this loan will not adjust.'
    tone = 'bg-amber-50 text-amber-800 border-amber-200'
  } else if (preview.indexMissing) {
    text = `Next adjustment ${when}: ${preview.indexMissing}. The loan is skipped until one is entered.`
    tone = 'bg-amber-50 text-amber-800 border-amber-200'
  } else if (!preview.termsComplete) {
    text = `Next adjustment ${when}: an index value and margin are needed before this loan can adjust.`
    tone = 'bg-amber-50 text-amber-800 border-amber-200'
  } else {
    const margin = Number((preview.fullyIndexedRate - preview.indexValue).toFixed(4))
    const limits = preview.appliedLimits?.length ? `, held by the ${preview.appliedLimits.join(' and ')}` : ''
    const change =
      preview.newRate === preview.currentRate
        ? `stays at ${preview.newRate}%`
        : `moves from ${preview.currentRate}% to ${preview.newRate}%`
    text =
      `Next adjustment ${when}: ${preview.indexSource} + ${margin}% margin = ` +
      `${preview.fullyIndexedRate}% fully indexed; the rate ${change}${limits}. ` +
      'Estimated from today’s values.'
  }

  const ready = !!when && !preview.indexMissing && preview.termsComplete

  const onAdjustNow = async () => {
    const ok = await confirm(
      `Schedule the ${when} adjustment now, at ${preview.newRate}%? It still takes effect on ${when}, and the ` +
        "borrower's rate change notice is filed to the loan's documents.",
    )
    if (!ok) return
    setAdjusting(true)
    try {
      const res: any = await adjustArmNow()
      if (res?.skip) toast(res.skip.reason, { type: 'warning' })
      else toast(`Scheduled: ${preview.newRate}% from ${when}. See Rate & Payment Changes.`, { type: 'success' })
      setReload((n) => n + 1)
      onAdjusted?.()
    } catch (err: any) {
      toast(err?.message || 'Could not schedule the adjustment', { type: 'error' })
    } finally {
      setAdjusting(false)
    }
  }

  return (
    <div className={`text-sm border rounded-lg px-3 py-2 flex items-start justify-between gap-3 ${tone}`}>
      <span>{text}</span>
      {ready && canAdjust && (
        <button
          className="shrink-0 text-shade-blue hover:underline font-medium disabled:opacity-50"
          disabled={adjusting}
          onClick={onAdjustNow}
        >
          {adjusting ? 'Scheduling…' : 'Adjust now'}
        </button>
      )}
    </div>
  )
}
